import { Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ClickAnalyticsPlugin, BehaviorMapValidator } from '@microsoft/applicationinsights-clickanalytics-js';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AppLoggerService {
  appInsights: ApplicationInsights;

  constructor() {
    this.configureAppInsights();
  }

  /**
   * configureAppInsights: method configures Application Insights with Click Analytics Plugin and telemetry initializer.
   */
  configureAppInsights = () => {
    const clickPluginInstance = new ClickAnalyticsPlugin();

    // Click Analytics configuration
    const behaviorMap = {
      NAVIGATION: 1, // Advancing to the next route within a webpage
    };

    const clickPluginConfig = {
      autoCapture: true,
      dataTags: {
        customDataPrefix: 'data-custom-',
        useDefaultContentNameOrId: true,
        parentDataTag: 'parent-group',
        donotTrackDataTag: 'dnt',
        captureAllMetaDataContent: true,
      },
      behaviorValidator: BehaviorMapValidator(behaviorMap),
      defaultRightClickBhvr: 'rc',
    };

    try {
      this.appInsights = new ApplicationInsights({
        config: {
          extensions: [clickPluginInstance],
          extensionConfig: {
            [clickPluginInstance.identifier]: clickPluginConfig,
          },
          instrumentationKey: environment.APPINSIGHTS_INSTRUMENTATIONKEY,
          connectionString: environment.APPLICATIONINSIGHTS_CONNECTION_STRING,
          enableAutoRouteTracking: true, // Option to log all route changes
        },
      });

      this.appInsights.loadAppInsights();

      const telemetryInitializer = (envelope: any) => {
        // Added below line because envelope.tags can be undefined in some cases.
        envelope.tags = envelope.tags || [];
        envelope.tags['ai.cloud.role'] = 'TestAI';
      };

      this.appInsights.addTelemetryInitializer(telemetryInitializer);
    } catch (error) {
      console.error(
        'Application insights was not configured. Ignore this error if running in debug mode.', error
      );
    }
  };

  /**
   * logPageView: method logs a page view event.
   * @param {string} name
   * @param {string} url
   */
  logPageView = (name?: string, url?: string) => {
    this.appInsights.trackPageView({
      name: name,
      uri: url,
    });
  };

  /**
   * logEvent: method logs a custom event.
   * @param {string} name
   * @param {object} properties
   */
  logEvent = (name: string, properties?: { [key: string]: any }) => {
    this.appInsights.trackEvent({ name: name }, properties);
  };

  /**
   * logMetric: method logs a custom metric.
   * @param {string} name 
   * @param {number} average 
   * @param {object} properties 
   */
  logMetric = (name: string, average: number, properties?: { [key: string]: any }) => {
    this.appInsights.trackMetric({ name: name, average: average }, properties);
  };

  /**
   * logException: method logs an exception.
   * @param {Error} exception
   * @param {number} severityLevel
   */
  logException = (exception: Error, severityLevel?: number) => {
    this.appInsights.trackException({
      exception: exception,
      severityLevel: severityLevel,
    });
  };

  /**
   * logTrace: method logs a trace message.
   * @param {string} message 
   * @param {object} properties 
   */
  logTrace = (message: string, properties?: { [key: string]: any }) => {
    this.appInsights.trackTrace({ message: message }, properties);
  };
}